// MCPConnectorPickerBody — first-class MCP connector creation modal.
//
// @symbo.ls/windows body-only modal (migrated 2026-06-10 with its sibling
// integration/library/new-project pickers — same recipe). Openers seed the
// draft then `el.call('openModal', { id: 'mcpConnectorPicker', template:
// 'custom', width: '510px', content: { extends: 'MCPConnectorPickerBody' },
// onClose: <clear draft> })`. This also un-dangles the /studio create-menu
// trigger — the old fixed overlay was only mounted on the project canvas
// (CanvasEditor) and the admin page, never on /studio.
// Root state contract:
//   root._mcpConnectorDraft       object  — form draft state
//   root._mcpConnectorSaving      boolean — in-flight flag
//
// On submit: calls el.call('saveMcpConnector', draft) — registered by the
// host page. Server discovers capabilities (tools/list + resources/list)
// and caches in the capabilities jsonb column.
//
// Design tokens and v3.14 syntax per CLAUDE.md:
//   - No raw CSS values — all tokens.
//   - Flat props access. Event handlers: onClick, onInput, onRender, onDestroy.
//   - placeholder: directly on input elements (no attr wrapper).
//   - Reactive CSS uses prop functions: background: (el, s) => ...
//   - State flag naming mirrors _genericIntFormOpen / _genericIntDraft.

const _DRAFT_KEY = '_mcpConnectorDraft'

// Derive a default connector name from a URL (hostname without www).
const _nameFromUrl = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '')
    return host.charAt(0).toUpperCase() + host.slice(1)
  } catch {
    return ''
  }
}

// Toast helper — mirrors IntegrationConfigureForm pattern.
const _toast = (el, payload) => {
  try {
    el.call('openNotification', payload)
  } catch (err) {
    console.warn('[mcp-connector]', payload.title, payload.message || '', err)
  }
}

// Submit handler. Delegates to registered project function 'saveMcpConnector'.
const _canvasSubmit = async (el, s) => {
  const draft = s.root?.[_DRAFT_KEY] || {}
  if (!draft.url) {
    _toast(el, {
      title: el.call('polyglot', 'canvas.mcp.error.urlRequiredTitle'),
      message: el.call('polyglot', 'canvas.mcp.error.urlRequiredMessage'),
      type: 'error'
    })
    return
  }
  s.root.update({ _mcpConnectorSaving: true })
  try {
    await el.call('saveMcpConnector', draft)
    s.root.update({
      [_DRAFT_KEY]: null,
      _mcpConnectorSaving: false
    })
    el.call('closeModal', 'mcpConnectorPicker')
    _toast(el, {
      title: el
        .call('polyglot', 'canvas.mcp.connected')
        .replace('{name}', draft.name || _nameFromUrl(draft.url)),
      type: 'success'
    })
    // Reload connector list if host exposes a loader.
    try {
      await el.call('reloadMcpConnectors')
    } catch {}
  } catch (err) {
    s.root.update({ _mcpConnectorSaving: false })
    _toast(el, {
      title: el.call('polyglot', 'canvas.mcp.error.connectFailedTitle'),
      message: err?.message || el.call('polyglot', 'canvas.mcp.error.unknown'),
      type: 'error'
    })
  }
}

// ── Transport mode buttons ────────────────────────────────────────────────────

const _transportModes = ['sse', 'websocket', 'http']
const _transportLabels = { sse: 'SSE', websocket: 'WebSocket', http: 'HTTP' }

const TransportModeBtn = {
  tag: 'button',
  type: 'button',
  minHeight: 'controlHCompact',
  padding: '0 Z',
  round: 'radiusPill',
  fontSize: 'Y2',
  fontWeight: '700',
  cursor: 'pointer',
  'aria-pressed': (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return String(active)
  },
  'aria-label': (el, s) => `Transport: ${s._label || s._mode || ''}`,
  // Fill-contrast segmented control (was a static `line` border on every
  // mode, border-habit sweep, design-direction.md §8). Inactive rest was
  // `transparent` (the outlined-ghost habit, since the border was its only
  // boundary) — now `overlay` + a hover wash, same conversion as
  // IntegrationScopeModeBtn. Active keeps the existing `blue` fill.
  background: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return active ? 'blue' : 'overlay'
  },
  color: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
    return active ? 'white' : 'title'
  },
  ':hover': {
    background: (el, s) => {
      const active = (s.root?.[_DRAFT_KEY]?.transport || 'sse') === s._mode
      return active ? 'blue' : 'hover'
    }
  },
  text: (el, s) => s._label || '',
  onClick: (e, el, s) => {
    s.root.update({
      [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), transport: s._mode }
    })
  }
}

// ── Auth kind mode buttons ────────────────────────────────────────────────────

const _authModes = ['none', 'bearer', 'api-key', 'oauth']
const _authLabels = { none: 'None', bearer: 'Bearer', 'api-key': 'API key', oauth: 'OAuth' }

const AuthModeBtn = {
  tag: 'button',
  type: 'button',
  minHeight: 'controlHCompact',
  padding: '0 Z',
  round: 'radiusPill',
  fontSize: 'Y2',
  fontWeight: '700',
  cursor: 'pointer',
  'aria-pressed': (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return String(active)
  },
  'aria-label': (el, s) => `Authentication: ${s._label || s._mode || ''}`,
  // Fill-contrast segmented control — same conversion as TransportModeBtn
  // above (border-habit sweep, design-direction.md §8).
  background: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return active ? 'blue' : 'overlay'
  },
  color: (el, s) => {
    const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
    return active ? 'white' : 'title'
  },
  ':hover': {
    background: (el, s) => {
      const active = (s.root?.[_DRAFT_KEY]?.authKind || 'none') === s._mode
      return active ? 'blue' : 'hover'
    }
  },
  text: (el, s) => s._label || '',
  onClick: (e, el, s) => {
    s.root.update({
      [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), authKind: s._mode }
    })
  }
}

// ── Main export ───────────────────────────────────────────────────────────────

export const MCPConnectorPickerBody = {
  // Body-only: the windows ModalWindow owns positioning, backdrop, ESC and
  // the header Close (brand ModalHeader delegate). onClose passed by the
  // openers clears the draft on every dismiss path.
  flow: 'y',
  gap: 'Z1',
  textAlign: 'start',

  ModalHeader: {
    Hgroup: {
      H: { text: '{{ canvas.mcp.title | polyglot }}' },
      P: { text: '{{ canvas.mcp.subtitle | polyglot }}' }
    },
    Icons: {
      // Brand ModalHeader pulls this group into the corner with a negative
      // right margin and adds horizontal group padding. Keep the vertical
      // alignment while lining the visible X up with the content below.
      margin: '-Y2 0 - auto',
      padding: '0',
      Close: {
        padding: 'Y2 0 Y2 Y2'
      }
    }
  },

  // One-click pre-fill for Symbols MCP — the primary novel design element.
  // Background cerulean.1 (cerulean accent per design brief).
  SymbolsMcpSuggest: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    gap: 'Z',
    align: 'center',
    minHeight: 'controlHCompact',
    padding: '0 Z',
    round: 'radiusPill',
    background: 'cerulean.1',
    color: 'cerulean',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'cerulean',
    fontSize: 'Y2',
    fontWeight: '700',
    cursor: 'pointer',
    title: (el) => el.call('polyglot', 'canvas.mcp.suggest.title'),
    Icon: { tag: 'span', text: '⚡', fontSize: 'Y2' },
    Label: { tag: 'span', text: '{{ canvas.mcp.suggest.label | polyglot }}', fontSize: 'Y2' },
    Hint: {
      tag: 'span',
      text: 'mcp.symbols.app',
      fontSize: 'Y1',
      color: 'cerulean',
      opacity: '0.75'
    },
    onClick: (e, el, s) => {
      s.root.update({
        [_DRAFT_KEY]: {
          ...(s.root?.[_DRAFT_KEY] || {}),
          url: 'https://mcp.symbols.app',
          name: 'Symbols MCP',
          transport: 'sse',
          authKind: 'oauth'
        }
      })
    }
  },

  // ── Name field ──────────────────────────────────────────────────────

  NameField: {
    flow: 'y',
    gap: 'V',
    NameLabel: {
      tag: 'label',
      for: 'mcp-connector-name',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: '{{ canvas.mcp.field.name | polyglot }}'
    },
    NameInput: {
      tag: 'input',
      id: 'mcp-connector-name',
      type: 'text',
      width: '100%',
      minHeight: 'controlH',
      padding: '0 Z2',
      round: 'radiusControl',
      theme: 'field',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: (el) => el.call('polyglot', 'canvas.mcp.field.namePlaceholder'),
      value: (el, s) => s.root?.[_DRAFT_KEY]?.name || '',
      // Imperatively sync DOM input.value on every render so controlled-input
      // reactivity works in v3.14 (state-driven value updates are not automatically
      // reflected by the browser's input.value property without an explicit write).
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.name || ''
        }
      },
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), name: e.target.value }
        })
      }
    }
  },

  // ── URL field ───────────────────────────────────────────────────────

  UrlField: {
    flow: 'y',
    gap: 'V',
    UrlLabel: {
      tag: 'label',
      for: 'mcp-connector-url',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: '{{ canvas.mcp.field.url | polyglot }}'
    },
    UrlInput: {
      tag: 'input',
      id: 'mcp-connector-url',
      type: 'url',
      width: '100%',
      minHeight: 'controlH',
      padding: '0 Z2',
      round: 'radiusControl',
      theme: 'field',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      // An example URL, not copy.
      placeholder: 'https://mcp.symbols.app',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.url || '',
      // Imperatively sync DOM input.value on every render so controlled-input
      // reactivity works in v3.14 (state-driven value updates are not automatically
      // reflected by the browser's input.value property without an explicit write).
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.url || ''
        }
      },
      onInput: (e, el, s) => {
        const url = e.target.value
        const draft = s.root?.[_DRAFT_KEY] || {}
        // Auto-derive name from URL if the user hasn't set one yet.
        const autoName = draft.name || _nameFromUrl(url)
        s.root.update({
          [_DRAFT_KEY]: { ...draft, url, name: autoName }
        })
      }
    }
  },

  // ── Transport picker ────────────────────────────────────────────────

  TransportField: {
    flow: 'y',
    gap: 'V',
    TransportLabel: {
      tag: 'label',
      id: 'mcp-transport-label',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: '{{ canvas.mcp.field.transport | polyglot }}'
    },
    TransportModes: {
      flow: 'x',
      gap: 'Z',
      role: 'group',
      'aria-labelledby': 'mcp-transport-label',
      childExtends: 'TransportModeBtn',
      childrenAs: 'state',
      children: _transportModes.map((m) => ({ _mode: m, _label: _transportLabels[m] }))
    }
  },

  // ── Auth kind picker ────────────────────────────────────────────────

  AuthField: {
    flow: 'y',
    gap: 'V',
    AuthLabel: {
      tag: 'label',
      id: 'mcp-auth-label',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: '{{ canvas.mcp.field.auth | polyglot }}'
    },
    AuthModes: {
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      role: 'group',
      'aria-labelledby': 'mcp-auth-label',
      childExtends: 'AuthModeBtn',
      childrenAs: 'state',
      children: _authModes.map((m) => ({ _mode: m, _label: _authLabels[m] }))
    }
  },

  // ── Auth token input (visible for bearer/api-key) ───────────────────

  AuthTokenField: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const kind = s.root?.[_DRAFT_KEY]?.authKind || 'none'
      return kind !== 'bearer' && kind !== 'api-key'
    },
    TokenLabel: {
      tag: 'label',
      for: 'mcp-connector-auth-token',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: (el, s) => {
        const kind = s.root?.[_DRAFT_KEY]?.authKind || 'none'
        return kind === 'bearer' ? 'Bearer token' : 'API key'
      }
    },
    TokenInput: {
      tag: 'input',
      id: 'mcp-connector-auth-token',
      type: 'password',
      width: '100%',
      minHeight: 'controlH',
      padding: '0 Z2',
      round: 'radiusControl',
      theme: 'field',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: '••••••••••••••••',
      value: (el, s) => s.root?.[_DRAFT_KEY]?.authToken || '',
      onRender: (el, s) => {
        if (el.node && document.activeElement !== el.node) {
          el.node.value = s.root?.[_DRAFT_KEY]?.authToken || ''
        }
      },
      onInput: (e, el, s) => {
        s.root.update({
          [_DRAFT_KEY]: { ...(s.root?.[_DRAFT_KEY] || {}), authToken: e.target.value }
        })
      }
    }
  },

  // ── Scope picker — reuses IntegrationScopePicker verbatim ───────────

  IntegrationScopePicker: {
    draftKey: _DRAFT_KEY,
    defaultScopeType: 'workspace'
  },

  // ── Actions row ─────────────────────────────────────────────────────

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button',
      type: 'button',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      // Ghost fill (was `transparent` + `line` outline — the outlined-button
      // habit, border-habit sweep, design-direction.md §8). Rest = `overlay`,
      // hover = the house `hover` wash — same conversion as
      // AiActionConfirmCard's RejectBtn (wave 1 precedent).
      background: 'overlay',
      color: 'title',
      fontSize: 'Z2',
      cursor: 'pointer',
      ':hover': { background: 'hover' },
      text: '{{ canvas.mcp.cancel | polyglot }}',
      onClick: (e, el, s) => {
        s.root.update({ [_DRAFT_KEY]: null })
        el.call('closeModal', 'mcpConnectorPicker')
      }
    },

    ConnectBtn: {
      tag: 'button',
      type: 'button',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      // A fixed brand-hue plate (no `theme:`), so the ink is fixed too — and it is
      // the brand `fillInk` (near-black), not white. White on a raw brand hue read
      // under the 4.5:1 floor in BOTH themes (blue 3.62, red 3.21, green 2.81);
      // near-black reads 5.66 / 6.39 / 7.28. Nika's decision 2026-09-21
      // (treatment 3, design-direction.md §6).
      background: 'blue',
      color: 'fillInk',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      disabled: (el, s) => !!s.root?._mcpConnectorSaving,
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?._mcpConnectorSaving ? 'canvas.mcp.connecting' : 'canvas.mcp.connect'
        ),
      onClick: async (e, el, s) => {
        await _canvasSubmit(el, s)
      }
    }
  }
}

// Re-export child components so host index.js can register them.
export { TransportModeBtn, AuthModeBtn }
